import { useState } from 'react';
import {
  apiErrorSummary,
  estimateWorkoutCalories,
  formatWeight,
  persistWorkout,
  type AthleteEnergy,
  type EffortScale,
  type Exercise,
  type MovementPattern,
  type NewWorkoutNote,
  type PRsByExercise,
  type Workout as WorkoutT,
} from '@jackedlogs/shared';

import { uuidv4 } from './uuid';
import { clearPersistedLoggerState } from './state/loggerDraftStorage';
import type {
  LoggerDispatch,
  LoggerDraft,
  WeightUnit,
} from './state/loggerDraft';
import {
  invalidateEntryHistoryCache,
  snapshotRecentBestE1rm,
} from './useEntryHistory';
import { buildPayload, detectOutliersForDraft } from './workoutMapping';
import type { LoggerMode } from './loggerMode';
import type { EditingTarget, MaxBumpInput } from './LoggerScreen';
import type { LoggerHostDeps } from './hostDeps';

// Save flow extracted verbatim out of LoggerScreen (Track R slice A).
// Owns the `submitting` flag + the proceed/onSave logic; the error,
// serverError, and postSaveSummary states stay in LoggerScreen (read by
// the render tree and cleared by the hydration effect) and are written
// through the setters passed in here. Behaviour-preserving — no path
// changed, only relocated.

type PostSaveSummary = { sessionId: string; onDone: () => void };

export type UseSaveWorkoutParams = {
  draft: LoggerDraft;
  editing: EditingTarget | null;
  mode: LoggerMode;
  catalog: Exercise[] | null;
  prHistory: PRsByExercise | null;
  defaultUnit: WeightUnit;
  effortScale: EffortScale;
  /** The athlete's energy demographics (bodyweight/age/sex), resolved from the
   *  Profile by the caller (resolveAthleteEnergy). Feeds the HC-5 workout
   *  calorie estimate persisted at save; absent fields degrade it honestly. */
  athlete: AthleteEnergy;
  /** i18n.resolvedLanguage — used for the outlier-prompt weight format. */
  resolvedLanguage: string | undefined;
  /** Auto-name suggestion ([[workoutNameSuggest]]): the fallback persisted
   *  as Workout.name when the user left the name blank. */
  nameSuggestion: string;
  dispatch: LoggerDispatch;
  persistedHydratedRef: { current: boolean };
  onSaved: (kind: 'sent' | 'queued', maxBumpInput?: MaxBumpInput) => void;
  onClose: () => void;
  /** recordsResource.refresh — refreshes the cached PR/records view. */
  refreshRecords: () => unknown;
  /** restTimer.reset — stop the rest banner + cancel the scheduled
   *  expiry notification when the workout ends. The sent-save path
   *  keeps the modal open in summary mode and defers the draft reset
   *  to the Done CTA, so the draft.startedAt effect (which tears the
   *  timer down on discard/queued) never fires here — the timer would
   *  otherwise keep ticking under the summary card. */
  resetRestTimer: () => void;
  setError: (v: string | null) => void;
  setServerError: (v: string | null) => void;
  setPostSaveSummary: (v: PostSaveSummary | null) => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
  deps: LoggerHostDeps;
};

export function useSaveWorkout({
  draft,
  editing,
  mode,
  catalog,
  prHistory,
  defaultUnit,
  effortScale,
  athlete,
  resolvedLanguage,
  nameSuggestion,
  dispatch,
  persistedHydratedRef,
  onSaved,
  onClose,
  refreshRecords,
  resetRestTimer,
  setError,
  setServerError,
  setPostSaveSummary,
  t,
  deps,
}: UseSaveWorkoutParams) {
  const [submitting, setSubmitting] = useState(false);

  // Persist the draft's timeline note events (Solution C) once the workout is
  // saved — once per save, never per tap. Each note is enqueued to its own
  // outbox keyed to the JUST-SAVED session id (the same `id` persistWorkout
  // mints), then drained: online they POST immediately, offline they ride the
  // reconnect drain like the workout itself. CREATE-only — the logger save
  // never DELETEs server notes, so it can't lose data; the note POST doesn't
  // depend on the session row existing yet (keyed by sessionId). Best-effort:
  // a note-flush failure must never fail the workout save (the workout is
  // already persisted). Card logger never sets noteEvents → no-op.
  const flushNoteEvents = async (sessionId: string) => {
    const events = draft.noteEvents ?? [];
    if (events.length === 0) return;
    for (const ev of events) {
      const body: NewWorkoutNote = {
        id: ev.id,
        sessionId,
        performedAt: ev.performedAt,
        text: ev.text,
        ...(ev.exerciseContextId
          ? { exerciseContextId: ev.exerciseContextId }
          : {}),
      };
      // Best-effort (deps.saveNoteEvent never throws): the workout is
      // already saved, so a note-flush failure must not fail the save.
      await deps.saveNoteEvent(body);
    }
  };

  // Persist a validated payload to the server (or the outbox on a
  // genuine network failure). Split out of onSave so the outlier-
  // warning Alert can re-enter the save flow when the user picks
  // "Save anyway" — buildPayload re-validation would be redundant by
  // then, the draft is the same as when the prompt opened.
  const proceedWithSave = async (
    built: Extract<ReturnType<typeof buildPayload>, { ok: true }>,
  ) => {
    setSubmitting(true);
    const id = uuidv4();
    // On an edit (write-then-cleanup mints a fresh session id), carry the
    // original session's createdAt through so the server preserves it
    // instead of re-stamping `now`. Repeat clones into a genuinely new
    // session, so it keeps a fresh createdAt (omit). Rides on the payload
    // so it also survives an offline edit replayed from the outbox.
    // `mode === 'log-past'` already implies a non-null editing target (see
    // deriveLoggerMode), but the compiler cannot see through the derivation:
    // the null check narrows `editing` for the reads below, it does not
    // re-derive the mode.
    const isEdit = editing !== null && mode === 'log-past';
    const payload =
      isEdit && editing.workout.createdAt
        ? { ...built.payload, createdAt: editing.workout.createdAt }
        : built.payload;
    // Movement pattern per exercise (from the draft entries, populated at
    // picker time) — used both for the HC-5 calorie estimate here and the
    // post-save MAX-bump eval below, so build it once.
    const exercisePatterns: Record<string, MovementPattern | null> = {};
    for (const e of draft.entries) {
      exercisePatterns[e.exerciseId] = e.movementPattern;
    }
    // HC-5: compute JL's own calorie estimate at save (uses the athlete's
    // then-current bodyweight) and persist it beside the measured value. Null
    // when nothing is estimable — then the field is simply absent.
    const caloriesEstimate = estimateWorkoutCalories(
      built.payload,
      exercisePatterns,
      athlete,
    );
    const workoutToPersist = caloriesEstimate
      ? { ...payload, caloriesEstimate }
      : payload;
    // Build a Workout-shaped object from the about-to-be-saved payload for
    // the post-save MAX-bump evaluation. Server-stamped fields (profileId,
    // createdAt, updatedAt) get placeholder values — the evaluator only
    // walks `entries[].sets`, so the placeholders are immaterial. The
    // exercise pattern map comes from the draft entries (populated at
    // picker time, see DraftEntry); entries without a pattern fall through
    // to the default rep-trust cap in suggestMaxesFromHistory's caller.
    //
    // ⚠️ Built HERE, above the try, and not in the success branch: the
    // offline branch needs the identical object (LG-35 — a workout saved in
    // airplane mode used to offer no MAX prompt at all, because
    // onSaved('queued') carried no maxBumpInput). Nothing in it depends on
    // the server's answer, so one construction serves both paths. Two
    // copies of this field list would be the classic pair that diverges.
    const justSavedWorkout: WorkoutT = {
      id,
      profileId: '',
      startedAt: built.payload.startedAt,
      ...(built.payload.endedAt ? { endedAt: built.payload.endedAt } : {}),
      ...(built.payload.name ? { name: built.payload.name } : {}),
      entries: built.payload.entries,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    // Snapshot the recent best e1RM per exercise BEFORE either branch
    // invalidates the history cache — the post-save MAX-regression gate
    // needs the pre-save window (recent PAST sessions, not the just-saved
    // one) so one light day can't lower a MAX below a recent stronger
    // session (Antenore 2026-06-26). Cold cache → empty map → legacy
    // behaviour.
    const recentBestE1RM = snapshotRecentBestE1rm(Date.now());
    const maxBumpInput: MaxBumpInput = {
      workout: justSavedWorkout,
      exercisePatterns,
      workoutId: id,
      recentBestE1RM,
    };
    try {
      // Try direct first — same model the web uses. On success, close
      // and let the caller refresh the list. On ApiError (server
      // replied with a 4xx/5xx), surface the actual server message
      // INLINE so the user can act on it instead of seeing a generic
      // "retry when online" that no retry will ever clear.
      await persistWorkout({
        workout: workoutToPersist,
        editingId: isEdit ? editing.sessionId : null,
        apiFetch: deps.apiFetch,
        generateId: () => id,
      });
      // Persist the timeline notes against the just-saved session id, before
      // the deferred draft reset wipes them.
      await flushNoteEvents(id);
      // Stay open in summary mode. Defer the reset + persistence
      // teardown + onClose to the Done CTA so the PostSessionSummaryCard
      // can read the draft-less server state via useSessionResult.
      // invalidateEntryHistoryCache + recordsResource.refresh run now
      // so the cached views the user navigates to (after Done) already
      // reflect this save. onSaved fires here too — the workouts-list
      // refresh is independent of the user's summary-mode dismissal.
      invalidateEntryHistoryCache();
      // The server recomputes this session's SessionResult on save, so drop
      // the shared progress cache too → the Progress panels refetch fresh
      // when next focused (perf B2).
      deps.invalidateSessionResults();
      void refreshRecords();
      // Workout ended — stop the rest timer now (banner + notification),
      // not at the deferred Done CTA, so it doesn't keep ticking under
      // the summary card.
      resetRestTimer();
      onSaved('sent', maxBumpInput);
      setPostSaveSummary({
        sessionId: id,
        onDone: () => {
          dispatch({ type: 'reset' });
          void clearPersistedLoggerState();
          persistedHydratedRef.current = false;
          setPostSaveSummary(null);
          onClose();
        },
      });
      setSubmitting(false);
      return;
    } catch (err) {
      // ⭐ LG-31 — the default is QUEUE, and the direction is the fix.
      // This used to read `if (isNetworkError(err))`, i.e. queue only when
      // we were POSITIVE it was the network. But isNetworkError opens by
      // rejecting every ApiError, and offline apiFetch throws a synthetic
      // ApiError(401, 'not_authenticated') as soon as getIdToken() can't
      // reach Cognito to refresh — so the queue branch was skipped and the
      // athlete got a server error for a workout the server never saw.
      // Leonardo hit exactly this on stable 4.13.1 (2026-08-07).
      // Now: queue unless a server actually returned a deterministic
      // verdict. A queue too many costs one idempotent replay; a queue too
      // few costs a training session.
      if (deps.shouldQueueError(err)) {
        // Enqueue with the same id so a future drain replays through the
        // API's idempotency middleware with stable keys derived from it.
        try {
          await deps.queueWorkout({
            id,
            workout: workoutToPersist,
            editingId: isEdit ? editing.sessionId : null,
          });
          // Queue the notes too (same session id) so they ride the same
          // reconnect drain as the offline workout, before the reset.
          await flushNoteEvents(id);
          dispatch({ type: 'reset' });
          resetRestTimer();
          void clearPersistedLoggerState();
          persistedHydratedRef.current = false;
          // Queued saves don't reach the server yet, but the draft is
          // gone from the UI — invalidate so the next Logger open
          // doesn't carry stale "this slug has no history" memory if
          // the outbox drains in between.
          invalidateEntryHistoryCache();
          // LG-35: the MAX prompt rides the offline path too. It used to
          // fire without maxBumpInput, so evaluateAfterSave bailed and a
          // workout logged in airplane mode never offered a new MAX at all
          // — the athlete had to notice and set it by hand, or lose it.
          // The evaluation reads the in-memory maxes cache, so it works
          // whenever the app has loaded them at some point this session;
          // a cold boot with no signal still skips it, deliberately (see
          // the maxesLoaded guard in useMaxBumpFlow).
          onSaved('queued', maxBumpInput);
          onClose();
        } catch (enqueueErr) {
          setError(
            enqueueErr instanceof Error
              ? enqueueErr.message
              : 'mobile.logger.errSaveFailed',
          );
        }
      } else {
        // A deterministic server verdict (400/404/422…). Replaying won't
        // clear it, so surface the actual server message INLINE — the user
        // can act on it, instead of a generic "retry when online" that no
        // retry will ever satisfy.
        //
        // ⚠️ This branch also takes 409 duplicate_id, and that is deliberate
        // — do NOT align it with writeOrQueue, where 409 means `done`. That
        // reading holds for a single-request write; a workout is two.
        // persistWorkout (workoutSave.ts:104-143) flips `sessionPosted` only
        // after the session POST resolves, so a 409 there means the sets
        // loop never ran and this attempt wrote no sets — "already exists"
        // is not "saved". Unreachable from here anyway: the session id is a
        // fresh uuid per attempt (:144). Pinned in useSaveWorkout.test.tsx.
        setServerError(apiErrorSummary(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSave = async () => {
    if (submitting) return;
    setError(null);
    setServerError(null);
    // Drop any set the athlete never marked done. The timeline FocusPanel
    // (the sole logger) always shows a "next set" input that may never be
    // LOG-SET; that proposed-but-not-done set must not be saved (Antenore
    // 2026-05-30; design §7.3).
    const built = buildPayload(draft, effortScale, {
      dropUncompleted: true,
      fallbackName: nameSuggestion,
    });
    if (!built.ok) {
      // Surface via Alert too — the inline error banner renders at the
      // bottom of the ScrollView while the Save button lives in the
      // header, so on validation failure the banner is off-screen and
      // the button "looks dead" (button-refactor regression). The
      // banner stays for anyone who scrolls; the Alert guarantees the
      // feedback reaches the user where their attention is.
      const message = built.reason.startsWith('mobile.logger.')
        ? t(built.reason)
        : built.reason;
      setError(built.reason);
      deps.notify(t('workout.savePromptTitle'), message);
      return;
    }
    // Outlier-weight prompt at save time. Two tiers (1.45.0):
    // history-based (3× the lifter's recent best for that exercise,
    // from /api/me/records — same aggregate driving the gold-pill PR
    // check) and an absolute equipment-ceiling fallback that catches
    // brand-new exercises where prHistory has nothing to compare
    // against. The 999 kg DB row that the old gate let through
    // silently now trips the dumbbell ceiling. Edit mode passes a
    // null prHistory by design (avoids self-comparison false
    // negatives on the PR check); the absolute fallback still fires
    // there so a typo in an edit is caught the same way.
    const outliers = detectOutliersForDraft(draft, prHistory, catalog);
    if (outliers.length > 0) {
      const locale = resolvedLanguage ?? 'en';
      const items = outliers
        .map((o) => {
          if (o.reason === 'reps') {
            return t('workout.outlierRepsItem', {
              exercise: o.exerciseName,
              reps: o.reps,
            });
          }
          if (o.reason === 'zero-weight') {
            return t('workout.outlierZeroWeightItem', {
              exercise: o.exerciseName,
              reps: o.reps,
            });
          }
          if (o.reason === 'e1rm-jump') {
            return t('workout.outlierE1rmItem', {
              exercise: o.exerciseName,
              e1rm: formatWeight(o.e1rmKg ?? 0, defaultUnit, locale),
              expected: formatWeight(o.expectedMaxKg, defaultUnit, locale),
            });
          }
          if (o.reason === 'per-implement-double') {
            return t('workout.outlierPerImplementItem', {
              exercise: o.exerciseName,
              weight: formatWeight(o.weightKg, defaultUnit, locale),
              expected: formatWeight(o.expectedMaxKg, defaultUnit, locale),
            });
          }
          return t('workout.outlierItem', {
            exercise: o.exerciseName,
            weight: formatWeight(o.weightKg, defaultUnit, locale),
            expected: formatWeight(o.expectedMaxKg, defaultUnit, locale),
          });
        })
        .join('\n• ');
      const message = `${t('workout.outlierPromptBody', {
        count: outliers.length,
      })}\n\n• ${items}`;
      const proceedAnyway = await deps.confirm({
        title: t('workout.savePromptTitle'),
        body: message,
        confirmLabel: t('workout.savePromptAnyway'),
        cancelLabel: t('workout.savePromptContinue'),
      });
      if (proceedAnyway) void proceedWithSave(built);
      return;
    }
    await proceedWithSave(built);
  };

  return { submitting, onSave };
}
